import '/assets/styles/modals/auth.scss';
import { useState, useMemo, useEffect, createContext, useContext } from 'react';

import config from '@wearetla/tla-essentials-tools/config';
import omit from 'lodash/omit';

// Partials
import { Form, Input, FormFeedback } from '/views/partials/forms';
import Btn from '/views/partials/btn';
import PinInput from '/views/partials/pin-input';
import Img from '@wearetla/tla-essentials-tools/partials/img';
import Turnstile from '/views/partials/turnstile';

// Functions
import formatPhone from '@wearetla/tla-essentials-tools/functions/format-phone';

// Services
import userServices from '/services/user';

// Context
import { useAuth } from '/utilities/auth';
import { useBreakpoints } from '@wearetla/tla-essentials-tools/utilities/breakpoints';
import { useModals } from '@wearetla/tla-essentials-tools/utilities/modals';
import { useTranslation } from '/utilities/i18n';
import { Trans } from 'react-i18next';

// Static Assets
import image_banner from '/assets/images/auth-modal/banner.jpg';
import image_banner_2x from '/assets/images/auth-modal/banner@2x.jpg';

const modalClasses = 'modal-auth mobile-full';

// Enums
const modes = {
	login: 'login',
	register: 'register',
	intro: 'intro',
};

const AuthContext = createContext({
	submitting: false,
	mode: modes.login,
	formData: null,
	setMode: () => {
		console.log('Auth Context not set up.');
	},
	submit: () => {
		console.log('Auth Context not set up.');
	},
});

const useAuthModal = () => useContext(AuthContext);

// Assets
const AuthModal = ({ closeBtn, close, setContainerClass }) => {
	const { desktop } = useBreakpoints();
	const { softLogin } = useAuth();
	const { t } = useTranslation();
	const [mode, setMode] = useState(desktop ? modes.login : modes.intro);
	const [formData, setFormData] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [feedbackData, setFeedbackData] = useState(null);

	// Pin
	const [pin, setPin] = useState('');
	const [otpSent, setOtpSent] = useState(false);
	const [pinError, setPinError] = useState(false);

	const isOtp = useMemo(() => !!formData?.phone && otpSent, [formData, otpSent]);
	const maskedPhone = useMemo(() => {
		if (formData?.phone) {
			const formattedPhone = formatPhone(formData.phone);
			return `${formattedPhone.substring(0, 9)}**${formattedPhone.substring(11, formattedPhone.length)}`;
		}
		return null;
	}, [formData]);

	const otpMessage = useMemo(() => {
		if (!otpSent) return null;
		if (otpSent.sms_send && otpSent.email_send) {
			return (
				<Trans
					i18nKey="{{phone}} numaralı telefonunuza ve kayıtlı e-posta adresinize gönderilen kodu aşağıdaki alana giriniz."
					values={{ phone: maskedPhone }}
				components={[<span key="phone" />]}
				>
					<span>{maskedPhone}</span>{' '}numaralı telefonunuza ve kayıtlı e-posta adresinize gönderilen kodu aşağıdaki alana giriniz.
				</Trans>
			);
		}
		if (otpSent.sms_send) {
			return (
				<Trans
					i18nKey="{{phone}} numaralı telefonunuza gelen SMS kodunu aşağıdaki alana giriniz."
					values={{ phone: maskedPhone }}
				components={[<span key="phone" />]}
				>
					<span>{maskedPhone}</span>{' '}numaralı telefonunuza gelen SMS kodunu aşağıdaki alana giriniz.
				</Trans>
			);
		}
		return t('Kayıtlı e-posta adresinize gönderilen kodu aşağıdaki alana giriniz.');
	}, [otpSent, maskedPhone, t]);

	useEffect(() => {
		setFormData(null);
		setFeedbackData(null);
		setContainerClass(`${modalClasses} mode-${mode}`);
	}, [mode]);

	const resendOtp = () => {
		setOtpSent(false);
	};

	const sendOtp = (phone, paramMode, email, turnstileToken) => {
		return new Promise((resolve, reject) => {
			setSubmitting(true);
			setFeedbackData(null);

			const otpMode = paramMode ?? mode;

			const additionalFields = {
				cf_turnstile_token: turnstileToken ?? formData.cf_turnstile_token,
				...(otpMode === 'register' ? {
					email: email ?? formData?.email,
				} : {}),
			};

			userServices
				.sendOTP(phone ?? formData?.phone, otpMode, additionalFields)
				.then((otpResult) => {
					setPin('');
					setSubmitting(false);
					setOtpSent(otpResult);
					resolve(true);
				})
				.catch((error) => {
					setFeedbackData(error);
					setSubmitting(false);
					reject(error);
				});
		});
	};

	const submitOtp = () => {
		if (pin.length === 6) {
			setSubmitting(true);
			setPinError(false);

			userServices
				.checkOTP(formData.phone, pin)
				.then((token) => {
					if (formData.mode === 'register') {
						const registerData = {
							...omit(formData, ['mode', 'user_term', 'kvkk_term']),
							otp_token: token,
							email_notify: formData.email_notify === '1',
							sms_notify: formData.sms_notify === '1',
							push_id: null,
							agreements: ['KVKK_TERM', 'USER_TERM'],
						};

						userServices
							.register(registerData)
							.then((payload) => {
								softLogin(payload.access_token)
									.then(() => {
										close();
									})
									.catch((error) => {
										setFeedbackData(error);
										setSubmitting(false);
									});
							})
							.catch((error) => {
								setFeedbackData(error);
								setSubmitting(false);
							});
					} else {
						userServices
							.login(formData.phone, token)
							.then((payload) => {
								softLogin(payload.access_token)
									.then(() => {
										close();
									})
									.catch((error) => {
										setFeedbackData(error);
										setSubmitting(false);
									});
							})
							.catch((error) => {
								setFeedbackData(error);
								setSubmitting(false);
							});
					}
				})
				.catch((error) => {
					setFeedbackData(error);
					setSubmitting(false);
				});
		} else {
			setPinError(true);
		}
	};

	const submit = (fields) => {
		sendOtp(fields.phone, fields.mode, fields.email, fields.cf_turnstile_token)
			.then(() => {
				setFormData(fields);
			})
			.catch((error) => {
				setFeedbackData(error);
			});
	};

	return (
		<AuthContext.Provider
			value={{
				submit,
				submitting,
				formData,
				mode,
				feedbackData,
				setMode,
			}}
		>
			{closeBtn}
			{desktop && (
				<div className="modal-bannerwrap">
					<Img
						cover
						src={image_banner}
						srcSet={`${image_banner_2x} 2x`}
						className="bannerwrap-banner"
					/>
				</div>
			)}
			<div className="modal-body">
				{isOtp ? (
					<div className="auth-pinform">
						<strong className="auth-title">{t('SMS Kodu')}</strong>
						<div className="auth-phonemessage">
							{otpMessage}
						</div>

						<FormFeedback className="auth-feedback" data={feedbackData} />

						<PinInput
							className="auth-pininput"
							disabled={submitting}
							error={pinError}
							onResend={resendOtp}
							onComplete={submitOtp}
							pin={pin}
							onChange={setPin}
						/>

						<Btn
							disabled={submitting}
							loading={submitting}
							onClick={submitOtp}
							className="pinform-submit primary block"
						>
							{mode === modes.login ? t('Giriş Yap') : t('Kayıt Ol')}
						</Btn>
					</div>
				) : (
					<>
						{mode === modes.intro && <Intro />}
						{mode === modes.register && <RegisterForm />}
						{mode === modes.login && <LoginForm />}
					</>
				)}
			</div>
		</AuthContext.Provider>
	);
};

const Intro = () => {
	const { setMode } = useAuthModal();
	const { t } = useTranslation();

	return (
		<>
			<strong className="auth-title">{t('Merhaba')}</strong>

			<div className="auth-intro">
				<Btn
					onClick={() => {
						setMode(modes.login);
					}}
					className="big text-big primary-text-contrast outline block intro-optbtn"
				>
					{t('Giriş Yapın')}
				</Btn>
				<span className="intro-separator">{t('veya')}</span>
				<Btn
					onClick={() => {
						setMode(modes.register);
					}}
					className="big text-big primary-text-contrast outline block intro-optbtn"
				>
					{t('Üye Olun')}
				</Btn>
			</div>
		</>
	);
};

const LoginForm = () => {
	const { submit, setMode, submitting, feedbackData, formData } = useAuthModal();
	const { t } = useTranslation();

	const onSubmit = (fields) => {
		submit({
			mode: 'login',
			...fields,
		});
	};

	return (
		<>
			<strong className="auth-title">{t('Giriş Yapın')}</strong>
			<Form onSubmit={onSubmit} className="auth-loginform grid-container">
				<FormFeedback inGrid className="auth-feedback" data={feedbackData} />
				<div className="row">
					<div className="col">
						<Input
							autoFocus
							disabled={submitting}
							value={formData?.phone ?? ''}
							type="phone"
							label={t('Telefon')}
							name="phone"
							autoComplete="tel"
							validation={{
								required: t('Telefon numaranızı girmelisiniz.'),
								phone: t('Geçerli bir telefon numarası girmelisiniz.'),
							}}
						/>
					</div>
				</div>

				{!!config.turnstile?.siteKey && (
					<div className="row">
						<div className="col">
							<Turnstile id="turnstile-login" />
						</div>
					</div>
				)}

				<div className="row">
					<div className="col">
						<Btn type="submit" disabled={submitting} loading={submitting} className="block primary">
							{t('Giriş Yap')}
						</Btn>
					</div>
				</div>
				<div className="row">
					<div className="col">
						<div className="auth-nav">
							{t('Hesabınız yok mu? Hemen')}{' '}
							<button
								onClick={() => {
									setMode(modes.register);
								}}
								type="button"
								className="hyperlink"
							>
								{t('Üye Olun')}
							</button>
							.
						</div>
					</div>
				</div>
			</Form>
		</>
	);
};

const RegisterForm = () => {
	const { submit, setMode, feedbackData, submitting, formData } = useAuthModal();
	const { openModal } = useModals();
	const { t } = useTranslation();

	const onSubmit = (fields) => {
		submit({
			mode: 'register',
			...fields,
		});
	};

	return (
		<>
			<strong className="auth-title">{t('Üye Olun')}</strong>
			<Form onSubmit={onSubmit} className="auth-registerform grid-container">
				<FormFeedback inGrid className="auth-feedback" data={feedbackData} />
				<div className="row">
					<div className="col x6 m-x6">
						<Input
							disabled={submitting}
							value={formData?.name ?? ''}
							type="text"
						label={t('Ad')}
						name="name"
						autoComplete="given-name"
						validation={{
							required: t('Adınızı girmelisiniz.'),
							}}
						/>
					</div>
					<div className="col x6 m-x6">
						<Input
							disabled={submitting}
							value={formData?.surname ?? ''}
							type="text"
						label={t('Soyad')}
						name="surname"
						autoComplete="family-name"
						validation={{
							required: t('Soyadınızı girmelisiniz.'),
							}}
						/>
					</div>
				</div>
				<div className="row">
					<div className="col">
						<Input
							disabled={submitting}
							value={formData?.email ?? ''}
							type="text"
						label={t('E-posta')}
						name="email"
						autoComplete="email"
						validation={{
							required: t('E-posta adresinizi girmelisiniz.'),
							email: t('Geçerli bir e-posta adresi girmelisiniz.'),
							}}
						/>
					</div>
				</div>
				<div className="row">
					<div className="col">
						<Input
							disabled={submitting}
							value={formData?.phone ?? ''}
							type="phone"
						label={t('Telefon')}
						name="phone"
						autoComplete="tel"
						validation={{
							required: t('E-posta adresinizi girmelisiniz.'),
							phone: t('Geçerli bir telefon numarası girmelisiniz.'),
							}}
						/>
					</div>
				</div>
				<div className="row">
					<div className="col auth-agreements">
						<Input
							disabled={submitting}
							checked={formData?.kvkk_term === '1'}
							className="agreements-checkbox multi-line"
							type="checkbox"
							label={
								<Trans
									i18nKey="<0>{{title}}</0> okudum ve içeriğini anladım."
									values={{ title: t('Kişisel Verilerin Korunması Hakkında Bilgilendirme metnini') }}
									components={[
										<button
											key="kvkk-term"
											type="button"
											className="hyperlink"
											onClick={() => {
												openModal('agreement', { layer: 2, agreement: 'KVKK_TERM' });
											}}
										/>,
									]}
								/>
							}
							uncheckedValue={false}
							defaultValue="1"
							name="kvkk_term"
							validation={{
								required: t('KVKK metnini onaylamanız.'),
							}}
						/>

						<Input
							disabled={submitting}
							checked={formData?.user_term === '1'}
							className="agreements-checkbox multi-line"
							type="checkbox"
							label={
								<Trans
									i18nKey="<0>{{title}}</0> okudum ve içeriğini anladım."
									values={{ title: t('Üyelik Sözleşmesi metnini') }}
									components={[
										<button
											key="user-term"
											type="button"
											className="hyperlink"
											onClick={() => {
												openModal('agreement', { layer: 2, agreement: 'USER_TERM' });
											}}
										/>,
									]}
								/>
							}
							uncheckedValue={false}
							defaultValue="1"
							name="user_term"
							validation={{
								required: t('Üyelik sözleşmesini onaylamanız.'),
							}}
						/>

						<Input
							disabled={submitting}
							checked={formData?.email_notify === '1'}
							className="agreements-checkbox multi-line"
							type="checkbox"
							label={t('Etkinlik Habercisi ile özel promosyon ve kampanya duyurularını E-posta yoluyla almak istiyorum.')}
							uncheckedValue="0"
							defaultValue="1"
							name="email_notify"
						/>

						<Input
							disabled={submitting}
							checked={formData?.sms_notify === '1'}
							className="agreements-checkbox multi-line"
							type="checkbox"
							label={t('Etkinlik Habercisi ile özel promosyon ve kampanya duyurularını SMS yoluyla almak istiyorum.')}
							uncheckedValue="0"
							defaultValue="1"
							name="sms_notify"
						/>
					</div>
				</div>
				{!!config.turnstile?.siteKey && (
					<div className="row">
						<div className="col">
							<Turnstile id="turnstile-register" />
						</div>
					</div>
				)}
				<div className="row">
					<div className="col">
						<Btn disabled={submitting} loading={submitting} type="submit" className="block primary">
							{t('Üye Ol')}
						</Btn>
					</div>
				</div>
				<div className="row">
					<div className="col">
						<div className="auth-nav">
							{t('Hesabınız var mı? Hemen')}{' '}
							<button
								onClick={() => {
									setMode(modes.login);
								}}
								type="button"
								className="hyperlink"
							>
								{t('Giriş Yapın')}
							</button>
							.
						</div>
					</div>
				</div>
			</Form>
		</>
	);
};

AuthModal.modalConfig = {
	containerClass: modalClasses,
};

export default AuthModal;
